import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import AccordionPanel from '@components/common/app-components/AccordionPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faExclamationCircle,
    faPlusCircle,
    faMinusCircle,
    faEdit,
} from '@fortawesome/free-solid-svg-icons';
import {
    fetchDmiVerificationsData,
    fetchDmiVlpVerificationsData,
    fetchHouseholdIncome,
} from '@actions/dmi/dmiActionCreators';
import AlertInfo from '@components/common/alert/AlertInfo';
import BackToDashboard from '@components/common/backToDashboard';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { configs } from '@constants/configs/configs';
import { FORMATTED_DATES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { useAppDispatch, useAppSelector, useModal } from '@hooks/index';
import { environmentSelector } from '@selectors/commonSelectors';
import { dateMethods, DATE_FORMAT } from '@utils/dates/dateMethods';
import { parseUTCDateWithFormat } from '@utils/dates/date.utils';
import {
    StyledAccordionContainer,
    StyledAccordionHeader,
    StyledAccordionHeaderText,
    StyledAlertInfoContainer,
    StyledBackButtonWrapper,
    StyledDetailsContainer,
    StyledContainerWrapper,
    StyledHeader,
    StyledSubheader,
    StyledSubheaderContainer,
    StyledUserInfo,
    StyledVerificationsContainer,
    StyledOverrideButton,
} from '../../../constants/styledComponents/DMIStyledComponents';
import { VERIFICATION } from '../constants';
import VerificationsTab from '../../../components/documentsVerification/VerificationsTab';
import VerificationsDocumentUpload from './VerificationsDocumentUpload';
import VerificationsVLP from './VerificationsVLP';
import { LANGUAGE_TYPE, languageTypes } from '@constants/common/languageTypes';
import { modalConfig } from '../modals/modalConfig';
import withModalWrapper from '@containers/common/WithModalWrapper';
import GIButton from '@components/common/button/GIButton';
import { fetchApplicationsDetails } from '@actions/cap/applicationsActionCreators';
import { camelCase } from '@utils/miscellaneous/objectHelpers';

const VerificationsContainer = () => {
    const dispatch = useAppDispatch();
    const { openModal, closeModal } = useModal();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const environment = useAppSelector(environmentSelector);
    const { data } = useAppSelector(state => state.dmi.verificationsData);
    const { activeTab, dashboardModels } = useAppSelector(state => state.individualPortal.dashboard);
    const { language, indPortalCsrOverrides } = useAppSelector(state => state.common.environment);
    const { caseNumber } = dashboardModels[activeTab] || {};
    const householdIncome = useAppSelector(state => state.dmi.householdIncome);
    const isShowOverrideButton = indPortalCsrOverrides || false;
    // Define the custom order
    const customOrder = [
        'Social Security Number',
        'Citizenship',
        'Lawful presence',
        'Death',
        'Incarceration Status',
        'Income',
        'Residency',
        'Non-ESI Minimum Essential Coverage',
        'Minimum Essential Coverage',
    ];

    const VLPkey = 'Lawful Presence';

    const selectedData = useMemo(() => {
        if (!Array.isArray(data?.mainApplicantList)) {
            return {};
        }
        // Ensure data is sorted before accessing the selected index
        sortApplicantDataByVerification(data);
        return data.mainApplicantList[selectedIndex] || {};
    }, [data, selectedIndex]);

    function sortApplicantDataByVerification(data) {
        data.mainApplicantList.forEach(applicant => {
            const { documentCategoryStatusMap } = applicant;

            const reorderedDocumentCategories = {};
            // Iterate over the custom order and push the corresponding data from original map
            customOrder.forEach(key => {
                if (documentCategoryStatusMap[key]) {
                    reorderedDocumentCategories[key] = documentCategoryStatusMap[key];
                }
            });

            // Now, add any keys from documentCategoryStatusMap that are not in customOrder
            Object.keys(documentCategoryStatusMap).forEach(key => {
                if (!reorderedDocumentCategories[key]) {
                    reorderedDocumentCategories[key] = documentCategoryStatusMap[key];
                }
            });

            // Split categories into verified and unverified
            const [verifiedCategories, unverifiedCategories] = Object.entries(reorderedDocumentCategories).reduce(
                ([verified, unverified]: any, [key, value]: any) => {
                    if (value.isDocumentCategoryVerified) {
                        verified.push([key, value]);
                    } else {
                        unverified.push([key, value]);
                    }
                    return [verified, unverified];
                },
                [[], []],
            );

            // Sort unverified categories in reverse order and combine
            const sortedEntries = [...unverifiedCategories, ...verifiedCategories];
            applicant.documentCategoryStatusMap = Object.fromEntries(sortedEntries);
        });
        return data;
    }

    useEffect(() => {
        const queryParams = new URLSearchParams(location?.search);
        const caseNumberFromURL = queryParams.get('caseNumber');
        const effectiveCaseNumber = caseNumber || caseNumberFromURL;
        dispatch(
            fetchDmiVerificationsData(
                configs.endpoints.DMI_GET_DMI_VERIFICATIONS.processReplacements({
                    caseNumber: effectiveCaseNumber,
                    language,
                }),
                environment,
            ),
        );
        if (environment?.currentCoverageYear) {
            dispatch(
                fetchApplicationsDetails(environment?.currentCoverageYear, environment, () => {
                    false;
                }),
            );
        }
    }, [caseNumber]);

    useEffect(() => {
        if (!householdIncome && data) {
            dispatch(
                fetchHouseholdIncome(
                    configs.endpoints.DMI_GET_HOUSEHOLD_INCOME.processReplacements({
                        applicationid: data?.applicationID,
                    }),
                    environment,
                ),
            );
        }
    }, [householdIncome, data]);

    useEffect(() => {
        if (data?.viewElectricVerification) {
            dispatch(
                fetchDmiVlpVerificationsData(configs.endpoints.DMI_VLP_VERIFICATIONS, {
                    applicantGuid: selectedData.applicantGuid,
                    applicationId: data.applicationID,
                }),
            );
        }
    }, [selectedData]);

    const getDocumentCategoryLabel = (language: LANGUAGE_TYPE, key: string, value) => {
        return language === languageTypes.ENGLISH ? key : value.documentCategoryLabel;
    };

    const handleOverrideModal = documentCategory => {
        const queryParams = new URLSearchParams(location?.search);
        openModal('overrideReasonModal', {
            applicantId: selectedData.applicantID,
            documentCategory: documentCategory,
            caseNumber: caseNumber || queryParams.get('caseNumber'),
        });
    };

    const getNotVerifiedText = (verificationStatus: string, lawfulPresenceNotVerified: boolean) => {
        if (verificationStatus === VERIFICATION.ROP_EXPIRED || lawfulPresenceNotVerified) {
            return VERIFICATION.NOT_VERIFIED_TEXT;
        }
        return VERIFICATION.NOT_VERIFIED_TEXT_ACTION_NEEDED;
    };

    return (
        <StyledVerificationsContainer className="grid-container gi-grid-container">
            <StyledBackButtonWrapper>
                <BackToDashboard />
            </StyledBackButtonWrapper>
            <StyledHeader data-testid={'dmi-verifications-header'}>
                <FormattedMessage id="dmi.applicantVerifications.header" />
            </StyledHeader>
            <StyledSubheaderContainer>
                <StyledSubheader data-testid={'dmi-verifications-subHeader'}>
                    <FormattedMessage id="dmi.applicantVerifications.subHeader" />
                </StyledSubheader>
            </StyledSubheaderContainer>
            <StyledContainerWrapper>
                <StyledUserInfo data-testid={'dmi-verifications-applicants'}>
                    {data &&
                        data?.mainApplicantList.map((value, index) => (
                            <VerificationsTab
                                name={value?.applicantDisplayName}
                                index={index}
                                selectedIndex={selectedIndex}
                                onSelect={() => setSelectedIndex(index)}
                                isDocumentCategoryVerified={value?.isDocumentCategoryVerified}
                                key={index}
                            />
                        ))}
                </StyledUserInfo>
                <StyledDetailsContainer>
                    {selectedData.verificationStatus !== VERIFICATION.NONE && (
                        <StyledAlertInfoContainer data-testid={'dmi-verifications-info'}>
                            <AlertInfo
                                id="dmi.applicantVerifications.alert.para"
                                textReplacements={{ ropDate: data?.ropCompleteDate, verificationStatus: selectedData.verificationStatus }}
                            />
                        </StyledAlertInfoContainer>
                    )}   
                    {data &&
                        Object.entries(selectedData?.documentCategoryStatusMap).map(([key, value]: any,index:number) => {
                            const lawfulPresenceNotVerified = key === VLPkey && selectedData.deniedQHPOnVLPFailure && !environment?.isState.ID;
                            return (
                                <StyledAccordionContainer
                                    key={`${selectedData.applicantDisplayName}-${key}`}
                                    data-testid={`dmi-verifications-${selectedData.applicantDisplayName}-${key}`}
                                >
                                    <AccordionPanel
                                        id={camelCase(`${selectedData.applicantDisplayName}-${key}-panel`)}
                                        ariaControlsId={`panel-${index}`}
                                        header={
                                            <StyledAccordionHeader>
                                                <StyledAccordionHeaderText
                                                    isDocumentCategoryVerified={value?.isDocumentCategoryVerified}
                                                >
                                                    <p className="verification-type mr-2 h6">
                                                        {getDocumentCategoryLabel(language, key, value)}
                                                    </p>
                                                    <p>
                                                        <FontAwesomeIcon
                                                            icon={
                                                                value?.isDocumentCategoryVerified
                                                                    ? faCheckCircle
                                                                    : faExclamationCircle
                                                            }
                                                            className="icon mr-2 xs"
                                                        />
                                                        <ConditionalRendering isTrue={!value?.isDocumentCategoryVerified}>
                                                            <FormattedMessage
                                                                id={getNotVerifiedText(value?.verificationStatus, lawfulPresenceNotVerified)}
                                                            />
                                                            <ConditionalRendering
                                                                isTrue={
                                                                    value?.verificationStatus !== VERIFICATION.ROP_EXPIRED && !lawfulPresenceNotVerified
                                                                }
                                                            >
                                                                <b>
                                                                    {value?.expirationDate ? dateMethods.getFormattedDate(
                                                                        FORMATTED_DATES.commonDate,
                                                                        parseUTCDateWithFormat(value?.expirationDate, DATE_FORMAT),
                                                                    ) : ''}
                                                                </b>
                                                            </ConditionalRendering>
                                                        </ConditionalRendering>
                                                        <ConditionalRendering isTrue={value?.isDocumentCategoryVerified}>
                                                            <FormattedMessage id={VERIFICATION.VERIFIED_TEXT} />
                                                        </ConditionalRendering>
                                                    </p>
                                                </StyledAccordionHeaderText>
                                                <ConditionalRendering
                                                    isTrue={!value?.isDocumentCategoryVerified && isShowOverrideButton && !lawfulPresenceNotVerified}
                                                >
                                                    <StyledOverrideButton>
                                                        <GIButton
                                                            data-variant="secondary"
                                                            icon={<FontAwesomeIcon icon={faEdit} />}
                                                            iconPlacement="start"
                                                            id={`override-${key}`}
                                                            isDisabled={false}
                                                            labelId="button.override"
                                                            name="override"
                                                            className={'btn-danger icon-edit-sign override-button'}
                                                            onClickHandler={() => {
                                                                handleOverrideModal(key);
                                                            }}
                                                            size="small"
                                                            type="button"
                                                            variant="ghost"
                                                        />
                                                    </StyledOverrideButton>
                                                </ConditionalRendering>
                                            </StyledAccordionHeader>
                                        }
                                        isExpandable={
                                            !value?.isDocumentCategoryVerified ||
                                            (key === VLPkey && data.viewElectricVerification)
                                        }
                                        isLeftToggle={true}
                                        expandedIcon={faMinusCircle}
                                        collapsedIcon={faPlusCircle}
                                    >
                                        <ConditionalRendering isTrue={!value?.isDocumentCategoryVerified}>
                                            <VerificationsDocumentUpload
                                                key={`${selectedData.applicantDisplayName}-${key}`}
                                                name={selectedData.applicantDisplayName}
                                                verificationType={key}
                                                ropDate={value?.expirationDate}
                                                documentTypeList={value?.documentTypeList}
                                                applicantId={selectedData.applicantID}
                                                documentCategory={value?.documentCategoryName}
                                                documentCategoryDetailList={value?.documentCategoryDtlList}
                                                isDocumentCategoryVerified={value?.isDocumentCategoryVerified}
                                                exchangeName={data?.exchangeName}
                                                sourceEn={selectedData?.sourceEn}
                                                sourceEs={selectedData?.sourceEs}
                                                lawfulPresenceNotVerified={lawfulPresenceNotVerified}
                                                incomeForFPL={selectedData.incomeForFPL}
                                            />
                                        </ConditionalRendering>
                                        <ConditionalRendering isTrue={key === VLPkey && data.viewElectricVerification}>
                                            <VerificationsVLP applicantGuid={selectedData.applicantGuid} />
                                        </ConditionalRendering>
                                    </AccordionPanel>
                                </StyledAccordionContainer>
                            );
                        })}
                </StyledDetailsContainer>
            </StyledContainerWrapper>
        </StyledVerificationsContainer>
    );
};

export default withModalWrapper(VerificationsContainer, modalConfig);